import React, { useEffect } from 'react';

import { faSparkles, faXmark } from '@fortawesome/pro-light-svg-icons';
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import useNeoLeoKBarActions from '../../../hooks/useNeoLeoKBarActions';
import AnalyticsService from '../../../services/AnalyticsService';
import { chatReply, saveShowIntro } from '../../../slices/LeoSlice';
import { AnalyticsEventEnum, AppDispatch, RootState } from '../../../types';
import {
  ChatFormData,
  ContextualPrompt,
  NeoLeoSearchResult,
  UserIntentEnum,
} from '../../../utils/LeoUtils';
import NeoLeoKBarActions from '../NeoLeoKBarActions';
import LeoControlledTextInput from './LeoControlledTextInput';

interface NeoLeoChatInputProps {
  setShowPrompts?(value: React.SetStateAction<boolean>): void;
  showPrompts?: boolean;
  isIntro?: boolean;
  askLeoText?: string;
}

const NeoLeoChatInput: React.FC<NeoLeoChatInputProps> = ({
  setShowPrompts,
  showPrompts,
  isIntro,
  askLeoText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { control, reset, setFocus } = useFormContext<ChatFormData>();
  const { session, isLeoThinking, currentChat, selectedItems, msgContext } =
    useSelector((state: RootState) => state.leo);
  const isDisabled = !session?.id || isLeoThinking;
  const actions = useNeoLeoKBarActions(askLeoText);

  const preSendChatReply = () => {
    reset({ askLeoText: '' });
    dispatch(saveShowIntro(false));
  };

  const sendChatReply = async (question: string, prompt?: ContextualPrompt) => {
    if (!session?.id) {
      return;
    }
    const updateSessionTitle =
      currentChat.length === 0 ||
      (currentChat.length === 1 &&
        currentChat[0].message === session?.disclaimer);
    await dispatch(
      chatReply(
        session?.id,
        question,
        updateSessionTitle,
        prompt,
        selectedItems.length
          ? [
              {
                type: msgContext?.details?.[0]?.type || '',
                items: selectedItems,
              },
            ]
          : undefined,
        location.pathname.slice(1),
        msgContext?.support_flow_details?.call
          ? UserIntentEnum.CALL_SUPPORT
          : msgContext?.support_flow_details?.ticket?.is_created === false
          ? UserIntentEnum.CREATE_TICKET
          : undefined,
      ),
    );
    AnalyticsService.instance().logEvent(
      AnalyticsEventEnum.LEO_MESSAGE_SEND_CLICK,
    );
  };

  const onSubmit = async (text: string) => {
    if (session?.id && !!text?.trim()) {
      preSendChatReply();
      await sendChatReply(text);
    }
  };

  const onActionClick = async (prompt: ContextualPrompt) => {
    if (session?.id) {
      preSendChatReply();
      await sendChatReply(prompt.prompt, prompt);
    }
  };

  useEffect(() => {
    if (isDisabled) {
      return;
    }
    setFocus('askLeoText');
  }, [isDisabled, setFocus]);

  return (
    <div className='mt-2 flex items-center gap-2 relative'>
      <div className='flex-1'>
        <LeoControlledTextInput<ChatFormData, 'askLeoText'>
          control={control}
          name='askLeoText'
          placeholder='Type something...'
          onSubmit={() => onSubmit(askLeoText!)}
          disabled={isDisabled}
          rightIcon={
            <button
              type='button'
              aria-label='leo-send'
              onClick={() => onSubmit(askLeoText!)}
              disabled={isDisabled || !askLeoText?.trim()}
            >
              <FontAwesomeIcon
                icon={faPaperPlaneTop}
                className={classNames('mt-1.5', 'text-reskin-aqua')}
              />
            </button>
          }
          shouldUnregister={false}
        />
      </div>
      {!isIntro && !askLeoText && (
        <button
          className='h-10 bg-white bg-opacity-10 aspect-square flex items-center justify-center rounded-full'
          type='button'
          onClick={() => setShowPrompts?.((prev) => !prev)}
        >
          {showPrompts ? (
            <FontAwesomeIcon
              icon={faXmark}
              className='text-reskin-aqua text-base'
            />
          ) : (
            <FontAwesomeIcon
              icon={faSparkles}
              className='text-reskin-aqua text-base'
            />
          )}
        </button>
      )}
      <NeoLeoKBarActions
        isOpen={!!askLeoText && Array.isArray(actions) && actions.length > 0}
        actions={actions as unknown as NeoLeoSearchResult[]}
        onActionClick={onActionClick}
        resetInput={() => {
          reset({ askLeoText: '' });
        }}
      />
    </div>
  );
};

export default NeoLeoChatInput;
